import React, { useEffect, useState } from 'react';

const Draggable: React.FC<{
  triggerOnLeftDrag: () => void;
  triggerOnRightDrag: () => void;
  wrapperClassName?: string;
  items?: any[];
  additionalProps?: any;
  template?: any;
  active?: number;
  children?: any;
  ignoreDrag?: boolean;
  gap?: number;
}> = ({
  triggerOnLeftDrag,
  triggerOnRightDrag,
  wrapperClassName,
  items,
  additionalProps,
  template,
  active,
  children,
  ignoreDrag,
  gap,
}) => {
  const PRETTY_GAP = gap ? gap : 102;
  const [mouseDown, setMouseDown] = useState(false);
  const [mouseMove, setMouseMove] = useState(false);
  const [mousePosition, setMousePosition] = useState<number | null>(null);
  const [state, setState] = useState(
    items
      ? [...items.map((item, index) => ({ ...item, left: PRETTY_GAP * index }))]
      : []
  );
  const [prev, setPrev] = useState<number | null>(null);
  const [translate, setTranslate] = useState(`translateX(0)`);
  const TRANSITION_DURATION = 700;
  const [duration, setDuration] = useState(`${TRANSITION_DURATION}ms`);
  const [shouldReset, setShouldReset] = useState(false);

  const dragHandler = (
    event: React.MouseEvent<HTMLDivElement> & React.TouchEvent<HTMLDivElement>
  ) => {
    if (ignoreDrag) {
      return false;
    }

    switch (true) {
      case ['mousedown', 'touchstart'].includes(event.type):
        if (!mouseDown) {
          setMouseDown(true);
          const mouseX = event.clientX
            ? event.clientX
            : event.changedTouches[0]?.clientX ?? 0;
          setMousePosition(mouseX);
        }
        break;
      case mouseDown && ['mousemove', 'touchmove'].includes(event.type):
        if (!mouseMove) {
          setMouseMove(true);
        } else if (mousePosition !== null) {
          const mouseX = event.clientX
            ? event.clientX
            : event.changedTouches[0]?.clientX ?? 0;
          const MIN_DRAG_PX = 50;
          const dragAmount = Math.abs(mousePosition - mouseX);
          const draggingRight = mousePosition - mouseX < 0;

          if (dragAmount > MIN_DRAG_PX) {
            if (draggingRight) {
              triggerOnRightDrag();
            } else {
              triggerOnLeftDrag();
            }

            setMouseDown(false);
            setMouseMove(false);
          }
        }
        break;
      case ['mouseup', 'touchend'].includes(event.type):
        setMouseDown(false);
        setMouseMove(false);
        break;
    }
  };

  const Template = template;

  const setTranslateX = (translateX: number, isAnimating: boolean) => {
    setTranslate(
      isAnimating
        ? `translate3d(${translateX}%, 0, 0)`
        : `translateX(${translateX}%)`
    );
  };

  useEffect(() => {
    if (ignoreDrag) {
      return;
    }

    if (!template) {
      return;
      // I coded myself into a corner here.
      // I need to figure out how to make this work without a template.
    }

    if (active === null || active === undefined) {
      return;
    }

    if (state.length === 0) {
      return;
    }

    const newState = [...state];
    setDuration(`${TRANSITION_DURATION}ms`);
    if (active === 0) {
      if (prev === newState.length - 1) {
        setShouldReset(true);
        setTranslateX(-PRETTY_GAP * newState.length, true);
      } else {
        newState[newState.length - 1].left = -PRETTY_GAP;
        setTranslateX(-PRETTY_GAP * active, true);
      }
    } else if (active === newState.length - 1) {
      if (prev === 0) {
        setShouldReset(true);
        setTranslateX(PRETTY_GAP, true);
      } else {
        newState[0].left = PRETTY_GAP * newState.length;
        setTranslateX(-PRETTY_GAP * active, true);
      }
    } else {
      newState[0].left = 0;
      newState[newState.length - 1].left = PRETTY_GAP * (newState.length - 1);
      setTranslateX(-PRETTY_GAP * active, true);
    }

    setState(newState);
    setPrev(active);
  }, [active]);

  useEffect(() => {
    if (ignoreDrag) {
      return;
    }

    if (!template) {
      return;
    }

    if (!shouldReset) {
      return;
    }

    setTimeout(() => {
      const newState = [...state];

      setDuration('0ms');

      if (active === 0) {
        newState[0].left = 0;
        newState[newState.length - 1].left = -PRETTY_GAP;
        setTranslateX(0, false);
      } else if (active === newState.length - 1) {
        newState[newState.length - 1].left = PRETTY_GAP * (newState.length - 1);
        newState[0].left = PRETTY_GAP * newState.length;

        setTranslateX(-PRETTY_GAP * (newState.length - 1), false);
      }

      setState(newState);
      setShouldReset(false);
    }, TRANSITION_DURATION);
  }, [shouldReset]);

  return (
    <div
      className={`draggable-trigger ${ignoreDrag ? 'draggable-trigger--ignore' : ''}`}
      onMouseDown={dragHandler}
      onTouchStart={dragHandler}
      onMouseUp={dragHandler}
      onTouchEnd={dragHandler}
      onMouseMove={dragHandler}
      onTouchMove={dragHandler}
    >
      <div
        className={wrapperClassName}
        style={{ transform: translate, transitionDuration: duration }}
      >
        {state &&
          state.map((item, index) => {
            return (
              <Template
                item={item}
                details={item}
                key={index}
                index={index}
                style={{ left: `${item.left}%` }}
                {...additionalProps}
              />
            );
          })}
        {children ? children : null}
      </div>
    </div>
  );
};

const DraggableSlider: React.FC<{
  itemsCount: number;
  activeIndex: number;
  state?: string;
  triggerOnClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ itemsCount, activeIndex, state, triggerOnClick }) => {
  const [elements, setElements] = useState(new Array(itemsCount).fill(0));
  const [active, setActive] = useState(activeIndex);

  const stateClass = state ? `draggable-slider--${state}` : '';
  const clickHandler = (
    event: React.MouseEvent<HTMLDivElement>,
    index: number
  ) => {
    triggerOnClick(event);
    setActive(index);
  };

  useEffect(() => {
    setActive(activeIndex);
  }, [activeIndex]);

  return (
    <div className={`draggable-slider ${stateClass}`}>
      {elements.map((element, index) => {
        const activeClass =
          active === index ? 'draggable-slider__item--active' : '';
        return (
          <div
            key={`draggable-slider-${index}`}
            className={`draggable-slider__item ${activeClass}`}
            onClick={event => clickHandler(event, index)}
            data-index={index}
          />
        );
      })}
    </div>
  );
};

export default Draggable;
export { DraggableSlider };
